import { API_BASE_URL, STORAGE_KEYS } from '../constants';
import { buildRouteDirectionLabel } from '../features/routes/routeSummaryProjection';

/** Единый вид названия города (без «г.», «город») — как на бэкенде RouteDirectionService. */
export function normalizeCityLabel(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim();
  if (!t) return null;
  const x = t.replace(/^г\.\s*/i, '').replace(/^город\s+/i, '').trim();
  return x || null;
}

export type GeocoderAddressComponent = { kind: string; name: string };

/**
 * Город из структурированных Components ответа Яндекс Геокодера (GeoObject → Address.Components).
 * locality, иначе последний province. Без split по строке address.
 */
export function extractCityFromGeocoderComponents(components: GeocoderAddressComponent[]): string | null {
  if (!Array.isArray(components) || components.length === 0) return null;
  const locality = components.find((c) => c.kind === 'locality');
  if (locality?.name) return normalizeCityLabel(locality.name);
  const provinces = components.filter((c) => c.kind === 'province');
  const province = provinces.length ? provinces[provinces.length - 1] : undefined;
  if (province?.name) return normalizeCityLabel(province.name);
  return null;
}

/**
 * Полный JSON ответ API geocode-maps.yandex.ru/1.x (корень с полем response).
 */
export function extractCityFromGeocoder(response: unknown): string | null {
  const features = (response as { response?: { GeoObjectCollection?: { featureMember?: unknown[] } } })?.response
    ?.GeoObjectCollection?.featureMember;
  if (!Array.isArray(features) || features.length === 0) return null;
  return extractCityFromGeocoderGeoObject((features[0] as { GeoObject?: unknown })?.GeoObject);
}

export function extractCityFromGeocoderGeoObject(geoObject: unknown): string | null {
  const addr = (geoObject as { metaDataProperty?: { GeocoderMetaData?: { Address?: { Components?: unknown } } } })
    ?.metaDataProperty?.GeocoderMetaData?.Address;
  const raw = addr?.Components;
  const components: GeocoderAddressComponent[] = Array.isArray(raw)
    ? raw
        .filter(
          (x: unknown): x is { kind: string; name: string } =>
            Boolean(x) && typeof x === 'object' && x !== null && 'kind' in x && 'name' in x
        )
        .map((x) => ({ kind: String((x as { kind: unknown }).kind), name: String((x as { name: unknown }).name) }))
    : [];
  return extractCityFromGeocoderComponents(components);
}

/**
 * Когда есть только строка адреса: один запрос к backend /address/search — city считается на сервере из Components.
 */
export async function resolveCityHintFromAddressString(address?: string | null): Promise<string | null> {
  const q = String(address ?? '').trim();
  if (q.length < 3) return null;
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const url = `${API_BASE_URL}/address/search?query=${encodeURIComponent(q.slice(0, 500))}`;
    const res = await fetch(url, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    const row = data[0] as { city?: string };
    const city = row?.city;
    return typeof city === 'string' && city.trim() ? normalizeCityLabel(city) : null;
  } catch {
    return null;
  }
}

/**
 * Извлекает город из адреса
 * Поддерживает различные форматы адресов:
 * - "г. Москва, ул. Ленина, 1"
 * - "Москва, ул. Ленина, 1"
 * - "Новосибирск, Красный проспект, 25"
 * - "Склад №1, г. Санкт-Петербург"
 */
export function extractCityFromAddress(address?: string): string {
  if (!address) return 'Не указан';
  const parts = address.split(',');
  return parts[0].trim() || 'Не указан';
}

/**
 * Создает строку направления маршрута в формате "Город1 → Город2"
 */
export const formatRouteDirection = (loadingAddress?: string, unloadingAddress?: string): string => {
  const fromCity = extractCityFromAddress(loadingAddress);
  const toCity = extractCityFromAddress(unloadingAddress);

  if (fromCity === 'Не указан' && toCity === 'Не указан') {
    return 'Не указано';
  }

  if (fromCity === 'Не указан') {
    return `? → ${toCity}`;
  }

  if (toCity === 'Не указан') {
    return `${fromCity} → ?`;
  }

  return `${fromCity} → ${toCity}`;
};

/**
 * Направление для заявки: города из БД; если нет — первый сегмент адреса (fallback для старых данных).
 * Не использует extractCityFromAddress.
 */
export function applicationRouteDirectionLabel(params: {
  loadingCity?: string | null;
  unloadingCity?: string | null;
  loadingAddress?: string | null;
  unloadingAddress?: string | null;
}): string {
  const head = (addr?: string | null): string => {
    if (!addr?.trim()) return '';
    const t = addr.trim();
    const i = t.indexOf(',');
    return (i >= 0 ? t.slice(0, i) : t).trim();
  };
  const fromRaw = (params.loadingCity?.trim() || head(params.loadingAddress)) || '';
  const toRaw = (params.unloadingCity?.trim() || head(params.unloadingAddress)) || '';
  if (!fromRaw && !toRaw) return 'Не указано';
  const from = fromRaw || '?';
  const to = toRaw || '?';
  return `${from} → ${to}`;
}

/** Данные для направления по цепочке стопов (GET /routes/:id с stops). */
export type RouteDirectionSource = {
  loadingCity?: string | null;
  unloadingCity?: string | null;
  loadingWarehouse?: { id?: number; city?: string | null } | null;
  unloadingWarehouse?: { id?: number; city?: string | null } | null;
  stops?: Array<{
    city?: string | null;
    address?: string | null;
    position?: number | null;
    type?: string | null;
    pointType?: string | null;
    pointId?: number | null;
    warehouse?: { city?: string | null } | null;
  }> | null;
};

/**
 * Направление только из stops (первый / последний по position) + structured city context.
 * Если стопов нет — «Не указано»; актуальные стопы подгружать через getRouteById.
 */
export function buildRouteDirection(route: RouteDirectionSource | null | undefined): string {
  if (!route) return 'Не указано';
  return buildRouteDirectionLabel(route.stops ?? [], {
    routeLoadingCity: route.loadingCity,
    routeUnloadingCity: route.unloadingCity,
    loadingWarehouse: route.loadingWarehouse,
    unloadingWarehouse: route.unloadingWarehouse
  });
}

/**
 * Универсальная функция для получения строки населённых пунктов маршрута
 */
export function getRouteDirection(route: any): string {
  if (!route) return '';
  let from = '';
  let to = '';
  if (route.transportType === 'auto') {
    from = route.loadingWarehouse && typeof route.loadingWarehouse === 'object' ? route.loadingWarehouse.city : '';
    to = route.unloadingWarehouse && typeof route.unloadingWarehouse === 'object' ? route.unloadingWarehouse.city : '';
    if (!from) from = extractCityFromAddress(route.loadingAddress || '');
    if (!to) to = extractCityFromAddress(route.unloadingAddress || '');
    return from && to ? `${from} → ${to}` : from || to || '';
  }
  if (route.transportType === 'railway') {
    from =
      route.departureStation && typeof route.departureStation === 'object'
        ? route.departureStation.city
        : route.departureStation;
    to =
      route.arrivalStation && typeof route.arrivalStation === 'object' ? route.arrivalStation.city : route.arrivalStation;
    return from && to ? `${from} → ${to}` : from || to || '';
  }
  if (route.transportType === 'aviation') {
    from =
      route.departureAirport && typeof route.departureAirport === 'object'
        ? route.departureAirport.city
        : route.departureAirport;
    to = route.arrivalAirport && typeof route.arrivalAirport === 'object' ? route.arrivalAirport.city : route.arrivalAirport;
    return from && to ? `${from} → ${to}` : from || to || '';
  }
  return '';
}
